import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Bot, Expand, MessageCircle, Send, Sparkles, X } from 'lucide-react';
import { getHelpResponse, getSuggestedQuestions, getWelcomeResponse } from '../utils/helpAssistant';

const createAssistantMessage = (response) => ({
  id: `${Date.now()}-${Math.random()}`,
  role: 'assistant',
  response,
});

const HelpAnswerModal = ({ response, isDark, onClose }) => {
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  if (!response) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/55 p-4 sm:p-6"
      onClick={onClose}
      role="presentation"
    >
      <div
        className={`relative flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl shadow-2xl ${
          isDark ? 'border border-gray-700 bg-gray-900 text-white' : 'border border-slate-200 bg-white text-gray-900'
        }`}
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="help-answer-title"
      >
        <div className={`flex items-start justify-between gap-4 border-b px-6 py-5 ${isDark ? 'border-gray-700 bg-gray-950' : 'border-slate-100 bg-blue-600 text-white'}`}>
          <div className="flex items-start gap-3">
            <div className={`mt-0.5 rounded-full p-2.5 ${isDark ? 'bg-blue-500/20 text-blue-300' : 'bg-white/15 text-white'}`}>
              <Bot className="h-6 w-6" />
            </div>
            <div>
              <p className={`text-xs font-semibold uppercase tracking-wide ${isDark ? 'text-blue-300' : 'text-blue-100'}`}>
                Yardım Robotu Cevabı
              </p>
              <h2 id="help-answer-title" className="mt-1 text-xl font-bold leading-snug sm:text-2xl">
                {response.title}
              </h2>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className={`rounded-full p-2 transition ${isDark ? 'text-gray-300 hover:bg-gray-800' : 'text-white hover:bg-white/10'}`}
            aria-label="Cevap penceresini kapat"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className={`flex-1 overflow-y-auto px-6 py-6 sm:px-8 sm:py-8 ${isDark ? 'bg-gray-900' : 'bg-white'}`}>
          <p className={`text-sm font-semibold uppercase tracking-wide ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
            Adımlar
          </p>
          <ol className={`mt-4 list-decimal space-y-4 pl-6 text-base leading-7 sm:text-lg sm:leading-8 ${isDark ? 'text-gray-100' : 'text-slate-800'}`}>
            {response.steps.map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ol>

          {response.hint && (
            <div className={`mt-8 rounded-2xl border px-5 py-4 ${isDark ? 'border-amber-500/30 bg-amber-500/10 text-amber-100' : 'border-amber-200 bg-amber-50 text-amber-950'}`}>
              <p className="text-sm font-bold uppercase tracking-wide">İpucu</p>
              <p className="mt-2 text-sm leading-6 sm:text-base">{response.hint}</p>
            </div>
          )}
        </div>

        <div className={`border-t px-6 py-4 ${isDark ? 'border-gray-700 bg-gray-950' : 'border-slate-100 bg-slate-50'}`}>
          <button
            type="button"
            onClick={onClose}
            className="w-full rounded-xl bg-blue-600 px-4 py-3 text-sm font-bold text-white transition hover:bg-blue-700 sm:w-auto sm:px-8"
          >
            Kapat
          </button>
        </div>
      </div>
    </div>
  );
};

const HelpAssistant = ({ currentPath = '/portal', isDark = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [messages, setMessages] = useState(() => [createAssistantMessage(getWelcomeResponse(currentPath))]);
  const [detailResponse, setDetailResponse] = useState(null);
  const messagesContainerRef = useRef(null);

  const suggestedQuestions = useMemo(() => getSuggestedQuestions(currentPath), [currentPath]);

  useEffect(() => {
    setMessages([createAssistantMessage(getWelcomeResponse(currentPath))]);
    setDetailResponse(null);
  }, [currentPath]);

  useEffect(() => {
    if (isOpen && messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  }, [isOpen, messages]);

  const openAnswerDetail = (response) => {
    setDetailResponse(response);
  };

  const submitQuestion = (question) => {
    const trimmedQuestion = question.trim();

    if (!trimmedQuestion) {
      return;
    }

    const answer = getHelpResponse(trimmedQuestion, currentPath);

    setMessages((prev) => [
      ...prev,
      {
        id: `${Date.now()}-user`,
        role: 'user',
        text: trimmedQuestion,
      },
      createAssistantMessage(answer),
    ]);
    setInputValue('');
    setIsOpen(true);
    openAnswerDetail(answer);
  };

  return (
    <>
      {detailResponse && (
        <HelpAnswerModal
          response={detailResponse}
          isDark={isDark}
          onClose={() => setDetailResponse(null)}
        />
      )}

      <div className="fixed bottom-5 right-5 z-[60]">
        {isOpen && (
          <div
            className={`mb-3 flex h-[32rem] w-[24rem] flex-col overflow-hidden rounded-2xl border shadow-2xl sm:w-[26rem] ${
              isDark ? 'border-gray-700 bg-gray-800 text-white' : 'border-gray-200 bg-white text-gray-900'
            }`}
          >
            <div className={`flex items-center justify-between px-4 py-3 ${isDark ? 'bg-gray-900' : 'bg-blue-600 text-white'}`}>
              <div className="flex items-center gap-3">
                <div className={`rounded-full p-2 ${isDark ? 'bg-blue-500/20 text-blue-300' : 'bg-white/15 text-white'}`}>
                  <Bot className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-sm font-bold">Yardım Robotu</p>
                  <p className={`text-xs ${isDark ? 'text-gray-300' : 'text-blue-100'}`}>Sorun, cevabı geniş pencerede açar</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className={`rounded-full p-2 transition ${isDark ? 'hover:bg-gray-700' : 'hover:bg-white/10'}`}
                aria-label="Yardım penceresini kapat"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className={`border-b px-4 py-3 ${isDark ? 'border-gray-700 bg-gray-900/40' : 'border-gray-100 bg-slate-50'}`}>
              <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide">
                <Sparkles className="h-4 w-4 text-orange-400" />
                <span>Hızlı Sorular</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {suggestedQuestions.map((question) => (
                  <button
                    key={question}
                    type="button"
                    onClick={() => submitQuestion(question)}
                    className={`rounded-full px-3 py-1.5 text-xs transition ${
                      isDark
                        ? 'bg-gray-700 text-gray-100 hover:bg-gray-600'
                        : 'bg-white text-gray-700 shadow-sm ring-1 ring-gray-200 hover:bg-blue-50'
                    }`}
                  >
                    {question}
                  </button>
                ))}
              </div>
            </div>

            <div
              ref={messagesContainerRef}
              className={`flex-1 space-y-3 overflow-y-auto px-4 py-4 ${isDark ? 'bg-gray-800' : 'bg-white'}`}
            >
              {messages.map((message) => (
                <div key={message.id} className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  {message.role === 'user' ? (
                    <div className="max-w-[85%] rounded-2xl rounded-br-md bg-blue-600 px-4 py-3 text-sm text-white shadow-sm">
                      {message.text}
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => openAnswerDetail(message.response)}
                      className={`max-w-[95%] rounded-2xl rounded-bl-md px-4 py-3 text-left text-sm shadow-sm transition hover:ring-2 ${
                        isDark
                          ? 'bg-gray-700 text-gray-100 hover:ring-blue-400/40'
                          : 'bg-slate-100 text-gray-800 hover:ring-blue-200'
                      }`}
                    >
                      <p className="font-semibold">{message.response.title}</p>
                      <p className={`mt-2 line-clamp-2 text-xs leading-5 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                        {message.response.steps[0]}
                      </p>
                      <span className={`mt-3 inline-flex items-center gap-1 text-xs font-bold ${isDark ? 'text-blue-300' : 'text-blue-600'}`}>
                        <Expand className="h-3.5 w-3.5" />
                        Cevabı geniş aç
                      </span>
                    </button>
                  )}
                </div>
              ))}
            </div>

            <form
              onSubmit={(event) => {
                event.preventDefault();
                submitQuestion(inputValue);
              }}
              className={`border-t px-4 py-3 ${isDark ? 'border-gray-700 bg-gray-900/60' : 'border-gray-100 bg-slate-50'}`}
            >
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={inputValue}
                  onChange={(event) => setInputValue(event.target.value)}
                  placeholder="Örn: Fatura nasıl oluşturulur?"
                  className={`flex-1 rounded-xl border px-3 py-2 text-sm outline-none transition ${
                    isDark
                      ? 'border-gray-600 bg-gray-800 text-white placeholder:text-gray-400 focus:border-blue-400'
                      : 'border-gray-200 bg-white text-gray-900 placeholder:text-gray-400 focus:border-blue-500'
                  }`}
                />
                <button
                  type="submit"
                  className="rounded-xl bg-blue-600 p-2.5 text-white transition hover:bg-blue-700"
                  aria-label="Soruyu gonder"
                >
                  <Send className="h-4 w-4" />
                </button>
              </div>
            </form>
          </div>
        )}

        <button
          type="button"
          onClick={() => setIsOpen((prev) => !prev)}
          className="flex items-center gap-2 rounded-full bg-blue-600 px-4 py-3 text-sm font-semibold text-white shadow-xl transition hover:bg-blue-700"
        >
          {isOpen ? <X className="h-4 w-4" /> : <MessageCircle className="h-4 w-4" />}
          Yardım Robotu
        </button>
      </div>
    </>
  );
};

export default HelpAssistant;
